import { createColumnHelper } from '@tanstack/react-table';
import { PortfolioVoucherOverview } from '@mews-commander/data-access';
import { LocalizedText, LocalizedWallClockDate, WallClockDateService } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ColumnFilterType, DropDownButton, DropdownItem, Icon, IconName, Link, Stack } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { DELETE_VOUCHER_STARTING_POINT, EDIT_VOUCHER_STARTING_POINT } from '../../../utilities/userFlowTestIds';

interface TableMappingProps {
    setVoucherToDelete: (voucher: PortfolioVoucherOverview | null) => void;
    setVoucherToEdit: (voucher: PortfolioVoucherOverview | null) => void;
    onNameClick: (voucher: PortfolioVoucherOverview) => void;
}

const columnHelper = createColumnHelper<PortfolioVoucherOverview>();

const RowActions = ({
    voucher,
    setVoucherToDelete,
    setVoucherToEdit,
}: {
    voucher: PortfolioVoucherOverview;
    setVoucherToDelete: (voucher: PortfolioVoucherOverview | null) => void;
    setVoucherToEdit: (voucher: PortfolioVoucherOverview | null) => void;
}) => (
    <DropDownButton icon={IconName.More} variant="ghost" size="small">
        <DropdownItem
            data-test-id={EDIT_VOUCHER_STARTING_POINT}
            leftDetail={<Icon value={IconName.Edit} />}
            label={<LocalizedText textKey={TextKey.Edit} />}
            onClick={() => setVoucherToEdit(voucher)}
        />
        <DropdownItem
            data-test-id={DELETE_VOUCHER_STARTING_POINT}
            leftDetail={<Icon value={IconName.Delete} />}
            label={<LocalizedText textKey={TextKey.Delete} />}
            onClick={() => setVoucherToDelete(voucher)}
        />
    </DropDownButton>
);

export const useColumns = ({ setVoucherToDelete, setVoucherToEdit, onNameClick }: TableMappingProps) => [
    columnHelper.accessor('name', {
        header: () => <LocalizedText textKey={TextKey.VoucherName} />,
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
        cell: (props) => (
            <Link standalone variant="basic" onClick={() => onNameClick(props.row.original)}>
                {props.row.original.name}
            </Link>
        ),
    }),
    columnHelper.accessor('enterpriseName', {
        header: () => <LocalizedText textKey={TextKey.Property} />,
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    columnHelper.accessor('companyName', {
        header: () => <LocalizedText textKey={TextKey.Company} />,
        cell: (props) => {
            const value = props.getValue();
            return value || UnicodeEntity.Dash;
        },
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    columnHelper.accessor('companyKey', {
        header: () => <LocalizedText textKey={TextKey.CompanyKey} />,
        cell: (props) => props.getValue() ?? UnicodeEntity.Dash,
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    columnHelper.accessor('key', {
        header: () => <LocalizedText textKey={TextKey.VoucherKey} />,
        cell: (props) => props.getValue() ?? UnicodeEntity.Dash,
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    columnHelper.accessor('rates', {
        header: () => <LocalizedText textKey={TextKey.AssignedRates} />,
        cell: (props) => {
            const value = props.getValue();

            if (!value.length) {
                return UnicodeEntity.Dash;
            }

            return (
                <Stack vertical>
                    {value.map((r) => (
                        <span key={`${props.row.original.id}-${r.id}`}>{r.name}</span>
                    ))}
                </Stack>
            );
        },
    }),
    columnHelper.accessor('occupiableIntervalStart', {
        id: 'occupiableIntervalStart',
        header: () => <LocalizedText textKey={TextKey.VoucherStayDateFrom} />,
        cell: (props) => {
            const value = props.getValue();
            return value != null ? (
                <LocalizedWallClockDate wallClockDate={WallClockDateService.parse(value)} />
            ) : (
                UnicodeEntity.Dash
            );
        },
        enableSorting: true,
    }),
    columnHelper.accessor('occupiableIntervalEnd', {
        id: 'occupiableIntervalEnd',
        header: () => <LocalizedText textKey={TextKey.VoucherStayDateTo} />,
        cell: (props) => {
            const value = props.getValue();
            return value != null ? (
                <LocalizedWallClockDate wallClockDate={WallClockDateService.parse(value)} />
            ) : (
                UnicodeEntity.Dash
            );
        },
        enableSorting: true,
    }),
    columnHelper.display({
        id: 'actions',
        header: '',
        meta: { align: 'end' },
        cell: (props) => (
            <RowActions
                voucher={props.row.original}
                setVoucherToDelete={setVoucherToDelete}
                setVoucherToEdit={setVoucherToEdit}
            />
        ),
    }),
];
